/* eslint-disable react/jsx-no-bind */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useSelector } from 'react-redux';
import Table from '../../components/Table';
import moment from 'moment';
import BrazilMoney from 'utils/BrazilMoney';
import { Chip } from '@mui/material';
import Dashboard from 'components/Bill/Dashboard';
import BillService from 'services/BillService';
import { checksIfThereIsAnOverdueInstallment } from 'utils';
import { useDependencyInjection } from 'hooks/useDependencyInjection';
import IconButtonDelete from 'components/Buttons/IconButtonDelete';

function PayBills() {
  const [payBills, setPayBills] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [openProgress, setOpenProgress] = useState(false);
  const [errors, setErrors] = useState([]);

  const [dateRange, setDateRange] = useState(null);

  const [totals, setTotals] = useState({ totalDueToday: 0, totalOverdueUnpaid: 0, totalDueFuture: 0 });

  const [openDropdown, setOpenDropdown] = useState(null);

  const nameProperty = useSelector((state) => state.nameProperty.nameProperty);

  const { success, error } = useDependencyInjection();

  const columns = [
    {
      title: 'Código',
      field: 'id',
      type: 'string',
      hideFilterIcon: true,
      editable: 'never',
    },
    {
      title: 'Descrição',
      field: 'description',
      type: 'string',
      hideFilterIcon: true,
      editable: 'never',
      render: (rowData) => rowData.description || 'Sem descrição',
    },
    {
      title: 'Vencimento',
      field: 'parts.expirationDate',
      type: 'date',
      hideFilterIcon: true,
      editable: 'never',
      dateSetting: { locale: 'pt-BR' },
      render: (rowData) => {
        const tmpData = rowData.parts[rowData.parts.length - 1] || {
          expirationDate: null,
        };
        return (
          <span>
            {tmpData.expirationDate === null
              ? ' - '
              : moment(tmpData.expirationDate.substring(0, 10)).format(
                'DD/MM/YYYY',
              )}
          </span>
        );
      },
    },
    {
      title: 'Pagamento',
      field: 'parts.paymentDate',
      type: 'date',
      hideFilterIcon: true,
      editable: 'never',
      dateSetting: { locale: 'pt-BR' },
      render: (rowData) => {
        const tmpData = rowData.parts[rowData.parts.length - 1];
        return (
          <span>
            {tmpData && tmpData.paymentDate === null
              ? ' - '
              : moment(tmpData && tmpData.paymentDate.substring(0, 10)).format(
                'DD/MM/YYYY',
              )}
          </span>
        );
      },
    },
    {
      title: 'Total',
      field: 'value',
      hideFilterIcon: true,
      editable: 'never',
      render: (rowData) => (
        <span>
          <BrazilMoney value={rowData.value} />
        </span>
      ),
    },
    {
      title: 'Parcelas',
      hideFilterIcon: true,
      editable: 'never',
      render: (rowData) => {
        const pay = rowData.parts.filter((part) => part.pay).length;
        const parts = rowData.parts.length;
        return (
          <p>{`${pay}/${parts}`}</p>
        );
      },
    },
    {
      title: 'Situação',
      hideFilterIcon: true,
      editable: 'never',
      type: 'string',
      render: (rowData) => {
        const situations = {
          Atrasada: { bgColor: '#EC928E', colorName: 'black' },
          Ativa: { bgColor: '#FFBB014D', colorName: 'black' },
          Recebida: { bgColor: '#AFFFD2', colorName: 'black' },
        };

        const pay = rowData.parts.filter((part) => part.pay).length;
        const parts = rowData.parts.length;

        const isOverdue = checksIfThereIsAnOverdueInstallment(rowData.parts);

        let situationName = '';
        if (pay < parts) {
          situationName = isOverdue ? 'Atrasada' : 'Ativa';
        } else if (pay === parts) {
          situationName = 'Recebida';
        }

        const { bgColor, colorName } = situations[situationName];

        return (
          <Chip
            style={{
              backgroundColor: bgColor,
              color: colorName,
              fontWeight: 500,
            }}
            label={situationName}
          />
        );
      },
    },
    {
      title: 'Fornecedor',
      field: 'stakeholder.name',
      type: 'string',
      hideFilterIcon: true,
      editable: 'never',
    },
    {
      title: 'Ação',
      hideFilterIcon: true,
      render: (rowData) => (
        <IconButtonDelete
          dialogMessage="A exclusão dessa conta pode levar a exclusão de objetos que dependam dela. Tem certeza que deseja excluir?"
          onClick={handleClickDel(rowData?.id)}
        />
      ),
    },
  ];

  const columnsForExport = [
    { title: 'Código', field: 'code' },
    { title: 'Descrição', field: 'description' },
    { title: 'Vencimento', field: 'expirationDate' },
    { title: 'Pagamento', field: 'paymentDate' },
    { title: 'Total', field: 'value' },
    { title: 'Parcelas', field: 'parts' },
    { title: 'Situação', field: 'status' },
    { title: 'Fornecedor', field: 'stakeholder' },
  ];

  const handleClickDel = (id) => async () => {
    try {
      await api
        .delete(`/bills/${id}`)
        .then(() => {
          success();
          loadBillList();
          loadBillsAmount();
        });
    } catch (err) {
      error(err.response.data.message);
    }
  };

  const loadBillList = useCallback(async () => {
    try {
      setOpenProgress(false);
      const response = await BillService.billListByTypeAndPeriod({ period: dateRange, type: 'pagamento' });
      if (response.length === 0) {
        setOpenProgress(true);
      }
      if (response) {
        setPayBills(response);
      }
    } catch (err) {
      throw new Error();
    } finally {
      setOpenProgress(true);
    }
  }, [dateRange]);

  useEffect(() => {
    loadBillList();
  }, [loadBillList, nameProperty]);

  const loadBillsAmount = useCallback(async () => {
    try {
      setOpenProgress(false);
      const response = await BillService.billsAmount({ period: dateRange, type: 'pagamento' });
      if (response) {
        const { totalDueFuture, totalDueToday, totalOverdueUnpaid } = response;
        setTotals({ totalDueFuture, totalDueToday, totalOverdueUnpaid });
      }
    } catch (err) {
      setErrors((prev) => [err.message, ...prev]);
    } finally {
      setOpenProgress(true);
    }
  }, [dateRange]);

  useEffect(() => {
    loadBillsAmount();
  }, [loadBillsAmount, nameProperty]);

  const loadBillsExport = useCallback(async () => {
    try {
      setOpenProgress(false);
      const response = await BillService.billsExport({ period: dateRange, type: 'pagamento' });
      if (response) {
        setExportData(response);
      }
    } catch (err) {
      throw new Error();
    } finally {
      setOpenProgress(true);
    }
  }, [dateRange]);

  useEffect(() => {
    loadBillsExport();
  }, [loadBillsExport, nameProperty]);

  function setData(newData) {
    const tempData = [];
    payBills.map((payBill) => (payBill.id === newData.id
      ? tempData.push(newData)
      : tempData.push(payBill)));
    setPayBills(tempData);
  }

  const handleOpenDropdown = (e) => {
    setOpenDropdown(e.currentTarget);
    e.stopPropagation();
  };

  return (
    <Dashboard
      title="Contas a pagar"
      dateRange={dateRange}
      setDateRange={setDateRange}
      handleOpenDropdown={handleOpenDropdown}
      openDropdown={openDropdown}
      setOpenDropdown={setOpenDropdown}
      totals={totals}
      loading={!openProgress}
    >
      <Table
        title="Pagamentos"
        titleCustomized=" "
        route="/pay-bills/"
        routeAdd="/pay-bill/"
        columns={columns}
        columnsForExport={columnsForExport}
        exportData={exportData}
        data={payBills}
        hasError={errors.length > 0}
        setData={setData}
        dataLoaded={openProgress}
      />
    </Dashboard>
  );
}

export default PayBills;
